import {
    buildBreakdownRows,
    collectPaginatedRows,
    countBy,
    createExportFileName,
    exportRide2FiveExcelWorkbook,
    sumBy,
    uniqueCountBy,
} from "@/lib/export/excel";

import { getBookings, getRideOffers, getTrips } from "../api/rideBookingsTripsApi";

const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return "—";
    try {
        return new Intl.DateTimeFormat("en-NG", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        }).format(new Date(dateStr));
    } catch {
        return dateStr;
    }
};

export const exportRideBookingsTripsWorkbook = async () => {
    const [rideOffers, bookings, trips] = await Promise.all([
        collectPaginatedRows({
            fetchPage: (page) => getRideOffers({ page, limit: 100 }),
            getRows: (response) => response.data.rideOffers ?? response.data.rides ?? [],
            getTotalPages: (response) => response.data.totalPages,
        }),
        collectPaginatedRows({
            fetchPage: (page) => getBookings({ page, limit: 100 }),
            getRows: (response) => response.data.bookings,
            getTotalPages: (response) => response.data.totalPages,
        }),
        collectPaginatedRows({
            fetchPage: (page) => getTrips({ page, limit: 100 }),
            getRows: (response) => response.data.trips,
            getTotalPages: (response) => response.data.totalPages,
        }),
    ]);

    const bookedSeatsAcrossOffers = sumBy(
        rideOffers,
        (ride) => ride.bookedSeats ?? ride.bookingsCount ?? ride.bookingCount ?? 0
    );
    const totalRideOfferRevenue = sumBy(
        rideOffers,
        (ride) => ride.revenue ?? ride.totalRevenue ?? ride.totalFare ?? 0
    );
    const totalBookingValue = sumBy(bookings, (booking) => booking.totalPrice);
    const totalTripFare = sumBy(trips, (trip) => trip.finalFare ?? trip.estimatedFare);
    const totalTripDistance = sumBy(trips, (trip) => trip.distanceKm);

    await exportRide2FiveExcelWorkbook({
        title: "Ride Bookings & Trips Export",
        subtitle:
            "Section-level Ride2Five ride operations export with separate sheets for ride offers, bookings, and trips.",
        fileName: createExportFileName("ride_bookings_trips_export"),
        filters: {
            scope: "Full section export",
            includes: "Ride offers, bookings, and trips",
        },
        metrics: [
            { label: "Ride Offers", value: rideOffers.length },
            { label: "Bookings", value: bookings.length },
            { label: "Trips", value: trips.length },
            {
                label: "Booked Seats Across Offers",
                value: bookedSeatsAcrossOffers,
            },
            {
                label: "Total Booking Value",
                value: totalBookingValue.toLocaleString("en-NG", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                }),
            },
            {
                label: "Total Trip Fare",
                value: totalTripFare.toLocaleString("en-NG", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                }),
            },
        ],
        sections: [
            {
                title: "Ride Offer Status Breakdown",
                rows: buildBreakdownRows(countBy(rideOffers, (ride) => ride.status)),
            },
            {
                title: "Booking Status Breakdown",
                rows: buildBreakdownRows(
                    countBy(bookings, (booking) => booking.bookingStatus)
                ),
            },
            {
                title: "Trip Status Breakdown",
                rows: buildBreakdownRows(countBy(trips, (trip) => trip.status)),
            },
            {
                title: "Operations Snapshot",
                rows: [
                    {
                        label: "Unique Drivers In Ride Offers",
                        value: uniqueCountBy(rideOffers, (ride) => ride.driverId),
                    },
                    {
                        label: "Unique Riders In Bookings",
                        value: uniqueCountBy(bookings, (booking) => booking.riderId),
                    },
                    {
                        label: "Unique Passengers In Trips",
                        value: uniqueCountBy(trips, (trip) => trip.passengerId),
                    },
                    {
                        label: "Total Ride Offer Revenue",
                        value: totalRideOfferRevenue.toLocaleString("en-NG", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        }),
                    },
                    {
                        label: "Total Trip Distance (km)",
                        value: totalTripDistance.toLocaleString("en-NG", {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1,
                        }),
                    },
                ],
            },
        ],
        sheets: [
            {
                sheetName: "Ride Offers",
                title: "Ride Offers Export",
                rows: rideOffers,
                columns: [
                    { header: "Ride Offer ID", width: 38, value: (ride) => ride.rideOfferId },
                    { header: "Driver ID", width: 38, value: (ride) => ride.driverId },
                    { header: "Driver Name", width: 22, value: (ride) => ride.driverName },
                    {
                        header: "Origin",
                        width: 28,
                        value: (ride) => ride.originAddress ?? ride.origin ?? "—",
                    },
                    {
                        header: "Destination",
                        width: 28,
                        value: (ride) =>
                            ride.destinationAddress ?? ride.destination ?? "—",
                    },
                    { header: "Departure Date", width: 16, value: (ride) => ride.departureDate ?? "—" },
                    { header: "Departure Time", width: 16, value: (ride) => ride.departureTime ?? "—" },
                    { header: "Status", width: 16, value: (ride) => ride.status },
                    { header: "Vehicle Type", width: 16, value: (ride) => ride.vehicleType },
                    {
                        header: "Price Per Seat",
                        width: 16,
                        alignment: "right",
                        value: (ride) => ride.pricePerSeat,
                        numFmt: "#,##0.00",
                    },
                    {
                        header: "Currency",
                        width: 12,
                        value: (ride) =>
                            ride.pricePerSeatCurrency ??
                            ride.currency ??
                            ride.totalFareCurrency ??
                            "NGN",
                    },
                    {
                        header: "Total Seats",
                        width: 12,
                        alignment: "right",
                        value: (ride) => ride.totalSeats,
                    },
                    {
                        header: "Booked Seats",
                        width: 12,
                        alignment: "right",
                        value: (ride) =>
                            ride.bookedSeats ?? ride.bookingsCount ?? ride.bookingCount ?? 0,
                    },
                    {
                        header: "Available Seats",
                        width: 12,
                        alignment: "right",
                        value: (ride) => ride.availableSeats ?? "—",
                    },
                    {
                        header: "Revenue",
                        width: 16,
                        alignment: "right",
                        value: (ride) =>
                            ride.revenue ?? ride.totalRevenue ?? ride.totalFare ?? 0,
                        numFmt: "#,##0.00",
                    },
                    {
                        header: "Ride Code",
                        width: 16,
                        value: (ride) => ride.rideCode ?? "—",
                    },
                    {
                        header: "Created At",
                        width: 22,
                        value: (ride) => formatDate(ride.createdAt),
                    },
                ],
            },
            {
                sheetName: "Bookings",
                title: "Bookings Export",
                rows: bookings,
                columns: [
                    { header: "Booking ID", width: 38, value: (booking) => booking.bookingId },
                    { header: "Booking Code", width: 18, value: (booking) => booking.bookingCode },
                    { header: "Ride Offer ID", width: 38, value: (booking) => booking.rideOfferId },
                    { header: "Rider ID", width: 38, value: (booking) => booking.riderId },
                    { header: "Rider Name", width: 22, value: (booking) => booking.riderName },
                    { header: "Driver ID", width: 38, value: (booking) => booking.driverId },
                    { header: "Driver Name", width: 22, value: (booking) => booking.driverName },
                    { header: "Pickup Address", width: 28, value: (booking) => booking.pickupAddress },
                    { header: "Dropoff Address", width: 28, value: (booking) => booking.dropoffAddress },
                    {
                        header: "Total Price",
                        width: 16,
                        alignment: "right",
                        value: (booking) => booking.totalPrice,
                        numFmt: "#,##0.00",
                    },
                    { header: "Currency", width: 12, value: (booking) => booking.currency },
                    {
                        header: "Seats",
                        width: 10,
                        alignment: "right",
                        value: (booking) => booking.numberOfSeats,
                    },
                    { header: "Booking Status", width: 18, value: (booking) => booking.bookingStatus },
                    { header: "Payment Status", width: 18, value: (booking) => booking.paymentStatus },
                    { header: "Ride Status", width: 18, value: (booking) => booking.rideStatus },
                    { header: "Booked At", width: 22, value: (booking) => formatDate(booking.bookedAt) },
                    {
                        header: "Confirmed At",
                        width: 22,
                        value: (booking) =>
                            booking.confirmedAt ? formatDate(booking.confirmedAt) : "—",
                    },
                    {
                        header: "Cancelled At",
                        width: 22,
                        value: (booking) =>
                            booking.cancelledAt ? formatDate(booking.cancelledAt) : "—",
                    },
                ],
            },
            {
                sheetName: "Trips",
                title: "Trips Export",
                rows: trips,
                columns: [
                    { header: "Trip ID", width: 38, value: (trip) => trip.tripId },
                    { header: "Ride Offer ID", width: 38, value: (trip) => trip.rideOfferId },
                    { header: "Booking ID", width: 38, value: (trip) => trip.bookingId },
                    { header: "Passenger ID", width: 38, value: (trip) => trip.passengerId },
                    { header: "Passenger Name", width: 22, value: (trip) => trip.passengerName },
                    { header: "Driver ID", width: 38, value: (trip) => trip.driverId },
                    { header: "Driver Name", width: 22, value: (trip) => trip.driverName },
                    { header: "Pickup Address", width: 28, value: (trip) => trip.pickupAddress },
                    { header: "Dropoff Address", width: 28, value: (trip) => trip.dropoffAddress },
                    { header: "Status", width: 18, value: (trip) => trip.status },
                    {
                        header: "Seats",
                        width: 10,
                        alignment: "right",
                        value: (trip) => trip.numberOfSeats,
                    },
                    {
                        header: "Estimated Fare",
                        width: 16,
                        alignment: "right",
                        value: (trip) => trip.estimatedFare,
                        numFmt: "#,##0.00",
                    },
                    {
                        header: "Final Fare",
                        width: 16,
                        alignment: "right",
                        value: (trip) => trip.finalFare ?? "—",
                        numFmt: "#,##0.00",
                    },
                    {
                        header: "Currency",
                        width: 12,
                        value: (trip) => trip.finalFareCurrency || trip.estimatedFareCurrency,
                    },
                    {
                        header: "Distance (km)",
                        width: 14,
                        alignment: "right",
                        value: (trip) => trip.distanceKm ?? "—",
                        numFmt: "#,##0.0",
                    },
                    {
                        header: "Duration (min)",
                        width: 14,
                        alignment: "right",
                        value: (trip) => trip.durationMinutes ?? "—",
                    },
                    {
                        header: "Driver Rating",
                        width: 12,
                        alignment: "right",
                        value: (trip) => trip.driverRating ?? "—",
                        numFmt: "#,##0.00",
                    },
                    {
                        header: "Passenger Rating",
                        width: 14,
                        alignment: "right",
                        value: (trip) => trip.passengerRating ?? "—",
                        numFmt: "#,##0.00",
                    },
                    { header: "Started At", width: 22, value: (trip) => (trip.startedAt ? formatDate(trip.startedAt) : "—") },
                    {
                        header: "Completed At",
                        width: 22,
                        value: (trip) =>
                            trip.completedAt ? formatDate(trip.completedAt) : "—",
                    },
                    {
                        header: "Cancelled At",
                        width: 22,
                        value: (trip) =>
                            trip.cancelledAt ? formatDate(trip.cancelledAt) : "—",
                    },
                    { header: "Created At", width: 22, value: (trip) => formatDate(trip.createdAt) },
                ],
            },
        ],
    });
};
